import { $config } from '@client/core/atoms/config.js';
import { waitForPulsePageViewId } from '@client/core/utils/pulse/pulse.js';
import pulse from '@finn-no/pulse-sdk';
import { debugLog } from '@schibsted-nmp/advertising-shared';

export abstract class PulseTracker {
  protected abstract readonly scope: string;

  protected async track(mapper: PulseTracker.EventData.Mapper) {
    let eventData: PulseTracker.EventData | null = null;

    try {
      eventData = await PulseTracker.EventData.make(mapper);
      pulse.trackEvent(eventData);

      debugLog(
        `[pulse] [${this.scope}] tracked ${PulseTracker.EventData.pretty(eventData)} event`,
        eventData
      );
    } catch (error) {
      const formattedEvent = eventData
        ? PulseTracker.EventData.pretty(eventData)
        : 'unknown';

      debugLog(
        `[pulse] [${this.scope}] failed to track ${formattedEvent} event`,
        error
      );
    }
  }
}

export namespace PulseTracker {
  export type EventData = pulse.TrackerData & {
    [P in 'type' | 'name' | 'object']-?: NonNullable<pulse.TrackerData[P]>;
  };

  export namespace EventData {
    export async function make(mapper: Mapper): Promise<EventData> {
      // Wait for the Pulse page view ID to be available before creating the event data:
      const pageState = await waitForPulsePageViewId();
      if (!pageState) throw new Error('Failed to obtain page state from pulse');

      const eventData = mapper(pageState);

      const vertical =
        eventData.vertical ?? pageState.vertical ?? getVerticalFromConfig();

      return {
        ...eventData,
        ...(vertical && { vertical })
      };
    }

    export type Mapper = (
      pageState: waitForPulsePageViewId.TrackerData
    ) => EventData;

    export function pretty({ type, intent, name }: PulseTracker.EventData) {
      return `${type}${intent ? ` to ${intent}` : ''} ${name}`;
    }
  }

  export function getVerticalFromConfig() {
    const config = $config.get();
    if (!config?.vertical) return null;

    const { vertical: name, subvertical: subVertical } = config;

    const vertical: pulse.TrackerData['vertical'] = {
      name,
      ...(subVertical && { subVertical })
    };

    return vertical;
  }
}
